import { QuestionCircleOutlined } from '@ant-design/icons';
import {
  Drawer,
  Form,
  Input,
  InputNumber,
  Popconfirm,
  Radio,
  Space,
  Switch,
  Tabs,
} from 'antd';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { RevxButton } from 'components/RevxButton';
import { RevxSpin } from 'components/RevxSpin';
import { EMPTY_MARKUP_RANGE, PartialMarkup } from 'domains/markupDomain';
import { merge } from 'lodash-es';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RULE_TEXT_LEN_255 } from 'utils/validators';
import ShippingTransfer from './ShippingTransfer';

const ShippingMarkupItemForm = () => {
  const { t } = useTranslation();
  const formItemLayout = {
    labelCol: { span: 5 },
    wrapperCol: { span: 18 },
  };
  const formActionsLayout = {
    labelCol: { span: 5 },
    wrapperCol: { span: 18, offset: 5 },
  };

  const [form] = Form.useForm();
  const dispatch = useAppDispatch();
  const saving = useAppSelector(
    (state) => state.loading.effects.markup.saveMarkup
  );
  const item = useAppSelector((state) => state.markup.curShippingMarkup);

  const onClose = () => {
    dispatch.markup.setState({ curShippingMarkup: undefined });
  };

  const [flatOrFree, setFlatOrFree] = useState<string>(
    item?.isFree ? 'free' : 'flat'
  );

  const onFinish = async (values: any) => {
    let updated: PartialMarkup = merge({}, item, values);
    if (flatOrFree === 'flat') {
      updated.isRange = false;
      updated.isFree = false;
      updated.markupRanges = updated.markupRanges!.slice(0, 1);
      updated.markupRanges![0].markupPct = values.markupPct;
      updated.markupRanges![0].hasHighCost = false;
      updated.markupRanges![0].lowCost = 0;
    }
    if (flatOrFree === 'free') {
      updated.isRange = false;
      updated.isFree = true;
      updated.markupRanges = [EMPTY_MARKUP_RANGE];
    }
    await dispatch.markup.saveMarkup(updated);
  };

  const onRemove = async () => {
    if (item?.id) {
      await dispatch.markup.removeMarkup(item);
      onClose();
    }
  };

  const actionBar = (
    <Form.Item {...formActionsLayout}>
      <Space>
        <RevxButton type="primary" htmlType="submit" brandPurple>
          {t('common.save')}
        </RevxButton>
        <RevxButton onClick={onClose}>{t('common.cancel')}</RevxButton>
        {item?.id && (
          <>
            <Popconfirm
              title={t('common.confirmQuestion')}
              icon={<QuestionCircleOutlined style={{ color: 'red' }} />}
              onConfirm={onRemove}
              okText={t('common.ok')}
              cancelText={t('common.cancel')}
            >
              <RevxButton danger>{t('common.remove')}</RevxButton>
            </Popconfirm>
          </>
        )}
      </Space>
    </Form.Item>
  );

  return (
    <Drawer
      destroyOnClose
      title={
        item?.id
          ? t('platform.markup.titles.edit')
          : t('platform.markup.titles.create')
      }
      placement="right"
      width={'600px'}
      closable={true}
      onClose={onClose}
      visible={Boolean(item)}
    >
      <RevxSpin spinning={saving}>
        <Tabs
          defaultActiveKey="1"
          style={{ height: '100%', backgroundColor: 'white' }}
        >
          <Tabs.TabPane tab={t('platform.markup.labels.markup')} key="1">
            <Form
              {...formItemLayout}
              layout={'horizontal'}
              form={form}
              initialValues={{
                ...item,
                markupPct: item?.markupRanges![0].markupPct || 0,
              }}
              onFinish={onFinish}
            >
              <Form.Item
                label={t('common.active')}
                name="active"
                valuePropName="checked"
              >
                <Switch disabled={!Boolean(item?.id)} />
              </Form.Item>

              <Form.Item
                label={t('common.description')}
                name="description"
                rules={[RULE_TEXT_LEN_255, { required: true }]}
              >
                <Input.TextArea rows={3} />
              </Form.Item>

              <Form.Item label={t('common.type')}>
                <Radio.Group
                  value={flatOrFree}
                  onChange={(e) => setFlatOrFree(e.target.value)}
                >
                  <Radio value="flat">
                    {t('platform.markup.labels.flatMarkup')}
                  </Radio>
                  <Radio value="free">
                    {t('platform.markup.labels.freeShipping')}
                  </Radio>
                </Radio.Group>
              </Form.Item>

              {flatOrFree === 'flat' && (
                <>
                  <Form.Item
                    label={t('platform.markup.labels.markup')}
                    name={'markupPct'}
                  >
                    <InputNumber<number>
                      min={-100}
                      max={100}
                      formatter={(v) => `${v}%`}
                      parser={(v) => Number(v?.replace('%', ''))}
                    ></InputNumber>
                  </Form.Item>
                </>
              )}
              {actionBar}
            </Form>
          </Tabs.TabPane>
          <Tabs.TabPane tab={t('platform.markup.labels.carriers')} key="2">
            <ShippingTransfer />
          </Tabs.TabPane>
        </Tabs>
      </RevxSpin>
    </Drawer>
  );
};
export default ShippingMarkupItemForm;
